import { useCallback, useEffect, useState } from 'react';

// CSC-295 — generic "do not show again" hook backed by localStorage. Uses a
// keyed namespace (`csc:dismissed:{key}`) so multiple banners can coexist
// without colliding on the storage. Re-renders consumers when the value flips
// so an in-app dismiss propagates immediately to siblings (e.g. one banner per
// permission-holder per session).
//
// Returns the same shape the caller would write themselves: `isDismissed` and a
// stable `dismiss()` callback.
const STORAGE_PREFIX = 'csc:dismissed:';

function readDismissed(key: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + key) === '1';
  } catch {
    // Storage may be disabled (Safari private mode, embedded contexts) — treat
    // as "not dismissed" so the banner still works for that session.
    return false;
  }
}

export function useDismissedBanner(key: string) {
  const [isDismissed, setIsDismissed] = useState<boolean>(() => readDismissed(key));

  // Sync across tabs — if the user dismisses in one tab the others should follow
  // without forcing a reload. The 'storage' event only fires on OTHER tabs, so
  // dismiss() also flips local state immediately.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_PREFIX + key) return;
      setIsDismissed(event.newValue === '1');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key]);

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(STORAGE_PREFIX + key, '1');
    } catch {
      // Ignore — the in-memory flip below still hides the banner for this session.
    }
    setIsDismissed(true);
  }, [key]);

  return { isDismissed, dismiss } as const;
}
